<template>
  <div class="tariff-change">
    <transition name="fade" mode="out-in">
      <loader-with-message v-if="productsLoading"
                           :key="'loadertariffs'"
                           :has-error="dataFetchError"
                           @retry="fetchUserTariffs"/>
      <div v-else :key="'tariffs'">
        <base-heading>{{ $t('changeTariff.pageTitle') }}</base-heading>
        <p v-if="statusChangeNotAvailable">{{ $t('changeTariff.status.notAvailable') }}</p>
        <template v-else>
          <p class="description">{{ $t('changeTariff.description') }}</p>
          <p v-if="isPending">{{ $t('changeTariff.status.pending') }}</p>
          <p v-else-if="statusChangeDisabled">{{ $t('changeTariff.status.disabled') }}</p>
          <p v-else-if="statusGreatestTariff">{{ $t('changeTariff.status.greatest') }}</p>
        </template>

        <div class="card-wrapper" v-if="productList.length">
          <tariff-card v-for="(product, index) in productList"
                       :key="'prod'+index"
                       :card-data="product"
                       :disabled="isPending"
                       @open-info="openInfoModal(index)"
                       @change-tariff="confirmTariffChangeFromCard(index)"/>
        </div>
      </div>
    </transition>

    <tariff-info-modal :visible="infoModalVisible"
                       :tariff-info="selectedTariff"
                       :isPending="isPending"
                       @close="infoModalVisible = false"
                       @change-tariff="confirmTariffChange"/>

    <confirmation-modal :visible="confirmChangeVisible"
                        :close-button-visible="false"
                        confirm-type="empty"
                        :confirm-title="$t('changeTariff.confirmModalTitle')"
                        :confirm-text="$t('changeTariff.changeTariffLabel')"
                        :dynamic-data-description="$t('changeTariff.confirmModalDescription', { name: selectedTariff.name})"
                        cancel-button
                        :loading="tariffChangeInProgress"
                        @close="confirmChangeVisible = false"
                        @confirm="submitTariffChange"/>

    <confirmation-modal :visible="successModalVisible"
                        confirm-type="success"
                        :confirm-title="$t('changeTariff.successModalTitle', { name: selectedTariff.name})"
                        :dynamic-data-description="$t('changeTariff.successModalDescription')"
                        :confirm-button="false"
                        @close="successModalVisible = false"/>
  </div>
</template>

<script>
import selectionBlockEvents from '@/mixins/selectionBlockEvents'
import TariffCard from '@/components/services/other/tariff-card'
import LoaderWithMessage from '@/components/common/loader-with-message'
import TariffInfoModal from '@/components/services/other/tariff-info-modal'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import errorMessageHandler from '@/mixins/errorMessageHandler'

export default {
  name: 'tariff-change',
  components: { ConfirmationModal, TariffInfoModal, LoaderWithMessage, TariffCard },
  data () {
    return {
      productsLoading: true,
      dataFetchError: false,
      productList: [],
      isPending: false,
      selectedTariff: {},
      infoModalVisible: false,
      confirmChangeVisible: false,
      successModalVisible: false,
      tariffChangeInProgress: false
    }
  },
  props: {
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      },
      required: true
    }
  },
  mixins: [selectionBlockEvents, errorMessageHandler],
  computed: {
    statusChangeDisabled () {
      return this.productList.every(product => product.remainingDays < 0)
    },
    statusGreatestTariff () {
      return this.productList.every(product => !product.canChange) && this.productList.length > 1
    },
    statusChangeNotAvailable () {
      return this.productList.every(product => !product.canChange) && this.productList.length === 1
    }
  },
  methods: {
    /**
     * Fetches tariffs for user. Returns a promise so we can unblock the sidebar once it's done
     */
    fetchUserTariffs () {
      const { subscriberIdentity } = this.$props.resourcesData

      if (!subscriberIdentity) return

      this.productsLoading = true
      return this.$apiRequest.get('user/tariffs?subscriberIdentity=' + subscriberIdentity)
        .then(response => {
          this.isPending = !!response.data.pendingDate
          this.productList = response.data.products || []
          this.productsLoading = false
        })
        .catch(e => {
          // handle error
          this.dataFetchError = true
        })
    },
    /**
     * Opens info modal for selected tariff
     * @param index - Number - index of the element in list used to get data from list
     */
    openInfoModal (index) {
      this.selectedTariff = {}
      setTimeout(() => {
        this.selectedTariff = { ...this.productList[index] }
        this.infoModalVisible = true
      }, 0)
    },
    /**
     * Opens confirm modal from card button
     * @param index {Number} Index of element in productList array
     */
    confirmTariffChangeFromCard (index) {
      this.selectedTariff = {}
      setTimeout(() => {
        this.selectedTariff = { ...this.productList[index] }
        this.confirmTariffChange()
      })
    },
    confirmTariffChange () {
      this.infoModalVisible = false
      this.confirmChangeVisible = true
    },
    submitTariffChange () {
      const { subscriberIdentity } = this.$props.resourcesData
      const { externalId } = this.selectedTariff

      if (!subscriberIdentity || !externalId) {
        this.handleErrorMessage({})
        return
      }

      const dataToSend = {
        subscriberIdentity: subscriberIdentity,
        tariffExternalId: this.selectedTariff.externalId
      }
      this.tariffChangeInProgress = true
      this.$apiRequest.post('user/tariffs/change', dataToSend)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.successModalVisible = true
          } else {
            this.handleErrorMessage({})
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.confirmChangeVisible = false
          this.tariffChangeInProgress = false
        })
    }
  },
  watch: {
    // ako stavim metodu u @close event, iz nekog razloga se duplira poziv.
    // TODO proveriti zašto
    successModalVisible (newVal) {
      if (!newVal) {
        this.fetchUserTariffs()
          .finally(() => {
            this.selectedTariff = {}
          })
      }
    }
  },
  mounted () {
    this.fetchUserTariffs()
      .finally(() => {
        this.unblockSidebarSelection(false)
      })
  }
}
</script>

<style scoped lang="scss">
.tariff-change {
  padding-top: 2rem;

  .card-wrapper {
    display: flex;
    flex-wrap: wrap;
    padding-top: 1rem;
    margin: 0 -1.6rem;
    max-width: calc(100% + 3.2rem);

    @media (map_get($breakpoints, md)) {
      margin: 0 -2.4rem;
      max-width: calc(100% + 4.8rem);
    }
  }

  .tariff-card {
    flex-basis: 100%;
    max-width: 100%;
    margin: 1.6rem;

    @media (map_get($breakpoints, sm)) {
      width: calc(50% - 3.2rem);
      flex-basis: calc(50% - 3.2rem);
    }

    @media (map_get($breakpoints, lg)) {
      margin: 2.4rem;
      width: calc(33% - 4.8rem);
      flex-basis: calc(33% - 4.8rem);
    }

    @media (map_get($breakpoints, xxl)) {
      width: 285px;
      flex-basis: 285px;
    }
  }
}
</style>
